import React, { useState } from "react";
import {
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  MenuItem,
  Box,
  Checkbox,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  Radio,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DirectionsCarFilledIcon from "@mui/icons-material/DirectionsCarFilled";
import DescriptionIcon from "@mui/icons-material/Description";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import NotesIcon from "@mui/icons-material/Notes";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn"; // Excel files
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"; // generic
import { Form } from "react-router-dom";
import { DeleteIcon, LucideDelete, Trash2 } from "lucide-react";

const StyledPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)",
  borderRadius: "20px",
  border: "1px solid rgba(0, 0, 0, 0.06)",
  boxShadow: "0 10px 40px rgba(0, 0, 0, 0.08)",
  padding: "48px",
  maxWidth: "1200px",
  margin: "0 auto",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    boxShadow: "0 15px 50px rgba(0, 0, 0, 0.12)",
  },
  [theme.breakpoints.down("md")]: {
    padding: "24px",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#ffffff",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    border: "2px solid rgba(0, 0, 0, 0.06)",
    fontSize: "15px",
    fontWeight: "500",
    color: "#1a1a1a",
    "& fieldset": {
      borderColor: "rgba(0, 0, 0, 0.06)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(25, 118, 210, 0.3)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "primary.main",
      borderWidth: "2px",
    },
    "&.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.1)",
      backgroundColor: "#ffffff",
    },
  },
  "& .MuiInputBase-input": {
    padding: "16px 16px",
    fontFamily: "'Inter', sans-serif",
    fontWeight: "500",
    "&::placeholder": {
      opacity: "0.6",
    },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "'Inter', sans-serif",
    fontWeight: "600",
    color: "#4a5568",
    fontSize: "14px",
    transform: "translate(14px, -12px) scale(0.75)",
    backgroundColor: "#ffffff",
    paddingLeft: "6px",
    paddingRight: "6px",
    marginLeft: "-3px",
    "&.Mui-focused": {
      color: "primary.main",
    },
  },
  "& .MuiFormHelperText-root": {
    fontFamily: "'Inter', sans-serif",
    fontSize: "13px",
    marginLeft: "0",
    color: "#718096",
    marginTop: "8px",
  },
}));

const SectionTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  marginBottom: "24px",
  marginTop: "32px",
  paddingTop: "24px",
  borderTop: "2px solid rgba(0, 0, 0, 0.05)",
  "&:first-of-type": {
    marginTop: "0",
    borderTop: "none",
    paddingTop: "0",
  },
});

const IconWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "28px",
  height: "28px",
  borderRadius: "8px",
  backgroundColor: "rgba(25, 118, 210, 0.1)",
  color: "primary.main",
  "& svg": {
    width: "16px",
    height: "16px",
  },
});

const SectionLabel = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontWeight: "700",
  fontSize: "14px",
  textTransform: "uppercase",
  letterSpacing: "1px",
  color: "primary.main",
});

const HeadingBox = styled(Box)({
  marginBottom: "40px",
  paddingBottom: "32px",
  borderBottom: "2px solid rgba(0, 0, 0, 0.05)",
});

const FormGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "28px",
  marginBottom: "8px",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: "20px",
  },
}));

const FullWidthGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gap: "28px",
  marginBottom: "8px",
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
    gap: "20px",
  },
}));

const FourColGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "28px",
  marginBottom: "20px",
  [theme.breakpoints.down("lg")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const AssistanceBox = styled(Box)(({ theme }) => ({
  padding: "20px",
  borderRadius: "12px",
  background:
    "linear-gradient(135deg, rgba(25, 118, 210, 0.04) 0%, rgba(25, 118, 210, 0.02) 100%)",
  border: "1px solid rgba(25, 118, 210, 0.1)",
  marginBottom: "24px",
  marginTop: "24px",
  display: "flex",
  // alignItems: "center",
  flexDirection: "column",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    borderColor: "rgba(25, 118, 210, 0.2)",
    backgroundColor: "rgba(25, 118, 210, 0.06)",
  },
}));

const NotesBox = styled(Box)({
  marginBottom: "8px",
});

const headingStyles = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontWeight: "800",
  fontSize: "30px",
  color: "#0a0e27",
  marginBottom: "12px",
  background: "linear-gradient(135deg, #0a0e27 0%, #1976d2 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
});

const subheadingStyles = styled(Typography)({
  fontFamily: "'Inter', sans-serif",
  fontSize: "15px",
  fontWeight: "500",
  color: "#718096",
  lineHeight: "1.6",
});

export default function PickupAddressForm({
  pickup,
  onChange,
  onToggle,
  errors,
  mode = "self",
}) {
  const [pickupType, setPickupType] = useState("own");
  const locationTypes = ["None", "Warehouse", "Factory", "Home", "Yard"];
  const equipmentTypes = ["None", "Forklift", "Crane", "Manual"];
  const timeSlots = [
    "None",
    "9:00 AM - 12:00 PM",
    "12:00 PM - 3:00 PM",
    "3:00 PM - 6:00 PM",
    "6:00 PM - 9:00 PM",
  ];
  const truckTypes = [
    "None",
    "Courier to 1 Tonne",
    "2 Tonne",
    "4 Tonne",
    "6 Tonne",
    "8 Tonne",
    "12 Tonne",
    "14 Tonne",
    "Semi",
  ];

  const hideExtraPickupFields = mode === "third";

  const handleFieldChange = (field) => (e) => {
    onChange(field)(e.target.value);
  };

  const handleSelectChange = (field) => (e) => {
    onChange(field)(e.target.value);
  };

  const handleToggleChange = (field) => (e) => {
    if (onToggle) onToggle(field)(e);
  };

  const handleFileUpload = (type) => (e) => {
    const files = Array.from(e.target.files);
    const current = pickup[type] || [];

    // Limit to 5 files
    const combined = [...current, ...files].slice(0, 5);

    onChange(type)(combined); // Update parent state
  };

  const handleDeleteFile = (type, index) => {
    const updatedFiles = pickup[type].filter((_, i) => i !== index);
    onChange(type)(updatedFiles); // Update parent state
  };

  const handlePickupTypeChange = (value) => {
    setPickupType(value); // Update local state for pickupType
    // If the parent needs to know about the change, you can call onChange here
    onChange("pickupType")(value); // This is optional if parent needs to be informed
  };

  return (
    <StyledPaper elevation={0}>
      <HeadingBox>
        <Typography
          variant="h4"
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontWeight: "800",
            fontSize: "30px",
            color: "#0a0e27",
            marginBottom: "12px",
            background: "linear-gradient(135deg, #0a0e27 0%, #1976d2 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Pickup Address
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Inter', sans-serif",
            fontSize: "15px",
            fontWeight: "500",
            color: "#718096",
            lineHeight: "1.6",
          }}
        >
          Enter your pickup location details to get started
        </Typography>
      </HeadingBox>

      {/* Pickup Type Section */}
      {!hideExtraPickupFields && (
        <>
          <SectionTitle>
            <IconWrapper>
              <PersonIcon />
            </IconWrapper>
            <SectionLabel>Pickup Type</SectionLabel>
          </SectionTitle>

          <Box sx={{ display: "flex", gap: 4, mb: 3 }}>
            <FormControlLabel
              control={
                <Radio
                  checked={pickupType === "own"}
                  onChange={() => handlePickupTypeChange("own")}
                />
              }
              label="Own Pickup"
            />

            <FormControlLabel
              control={
                <Radio
                  checked={pickupType === "third-party"}
                  onChange={() => handlePickupTypeChange("third-party")}
                />
              }
              label="Third Party"
            />
          </Box>
        </>
      )}

      {/* Contact Information Section */}
      <SectionTitle>
        <IconWrapper>
          <PersonIcon />
        </IconWrapper>
        <SectionLabel>Contact Information</SectionLabel>
      </SectionTitle>
      <FormGrid>
        <StyledTextField
          fullWidth
          label="First Name *"
          value={pickup.firstName}
          onChange={handleFieldChange("firstName")}
          error={!!errors?.firstName}
          helperText={errors?.firstName}
          variant="outlined"
        />
        <StyledTextField
          fullWidth
          label="Last Name *"
          value={pickup.lastName}
          onChange={handleFieldChange("lastName")}
          error={!!errors?.lastName}
          helperText={errors?.lastName}
          variant="outlined"
        />
        <StyledTextField
          fullWidth
          label="Contact Phone Number *"
          value={pickup.phone}
          onChange={handleFieldChange("phone")}
          error={!!errors?.phone}
          helperText={errors?.phone}
          variant="outlined"
        />
        <StyledTextField
          fullWidth
          label="Company Name *"
          value={pickup.companyName}
          onChange={handleFieldChange("companyName")}
          error={!!errors?.companyName}
          helperText={errors?.companyName}
          variant="outlined"
        />
      </FormGrid>

      {/* Location Section */}
      <SectionTitle>
        <IconWrapper>
          <LocationOnIcon />
        </IconWrapper>
        <SectionLabel>Location Details</SectionLabel>
      </SectionTitle>
      <FormGrid>
        <StyledTextField
          fullWidth
          label="Country *"
          value={pickup.country}
          onChange={handleFieldChange("country")}
          error={!!errors?.country}
          helperText={errors?.country}
          variant="outlined"
        />
        <StyledTextField
          fullWidth
          label="State / Province *"
          value={pickup.state}
          onChange={handleFieldChange("state")}
          error={!!errors?.state}
          helperText={errors?.state}
          variant="outlined"
        />
        <StyledTextField
          fullWidth
          label="City *"
          value={pickup.city}
          onChange={handleFieldChange("city")}
          error={!!errors?.city}
          helperText={errors?.city}
          variant="outlined"
        />
        <StyledTextField
          fullWidth
          label="Postcode *"
          value={pickup.postcode}
          onChange={handleFieldChange("postcode")}
          error={!!errors?.postcode}
          helperText={errors?.postcode}
          variant="outlined"
        />
      </FormGrid>

      {/* Address Section */}
      <SectionTitle>
        <IconWrapper>
          <HomeIcon />
        </IconWrapper>
        <SectionLabel>Address</SectionLabel>
      </SectionTitle>

      <FullWidthGrid>
        <StyledTextField
          fullWidth
          label="Address Line 1 *"
          value={pickup.addressLine1}
          onChange={handleFieldChange("addressLine1")}
          error={!!errors?.addressLine1}
          helperText={errors?.addressLine1}
          variant="outlined"
        />
        <StyledTextField
          fullWidth
          label="Address Line 2 (Optional)"
          value={pickup.addressLine2}
          onChange={handleFieldChange("addressLine2")}
          error={!!errors?.addressLine2}
          helperText={errors?.addressLine2}
          variant="outlined"
        />
      </FullWidthGrid>

      {/* Logistics Section */}
      <SectionTitle>
        <IconWrapper>
          <LocalShippingIcon />
        </IconWrapper>
        <SectionLabel>Logistics & Equipment</SectionLabel>
      </SectionTitle>
      <FormGrid>
        <StyledTextField
          select
          fullWidth
          label="Location Type *"
          value={pickup.locationType || "None"}
          onChange={handleSelectChange("locationType")}
          error={!!errors?.locationType}
          helperText={errors?.locationType}
          variant="outlined"
        >
          {locationTypes.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </StyledTextField>
        <FormControlLabel
          control={
            <Checkbox
              checked={pickup.forkliftAvailable}
              onChange={(e) => onToggle("forkliftAvailable")(e.target.checked)}
            />
          }
          label="Forklift Availability"
        />
      </FormGrid>

      {/* Assistance Section */}
      <AssistanceBox>
        <FormControlLabel
          control={
            <Switch
              checked={!!pickup.assistanceAvailable}
              onChange={handleToggleChange("assistanceAvailable")}
              sx={{
                "& .MuiSwitch-switchBase": {
                  color: "#cbd5e0",
                  "&.Mui-checked": {
                    color: "primary.main",
                    "& + .MuiSwitch-track": {
                      backgroundColor: "primary.main",
                    },
                  },
                },
                "& .MuiSwitch-track": {
                  backgroundColor: "#cbd5e0",
                },
              }}
            />
          }
          label={
            <Typography fontWeight={600}>
              {pickup.assistanceAvailable
                ? "Assistance Available at Pickup"
                : "No Assistance at Pickup"}
            </Typography>
          }
        />
        {pickup.assistanceAvailable && (
          <>
            <SectionTitle >
              <IconWrapper>
                <NotesIcon /> 
              </IconWrapper>
              <SectionLabel>Special Assistance Notes</SectionLabel>
            </SectionTitle>
            <StyledTextField
              fullWidth
              label="Special Assistance Notes (Optional)"
              multiline
              minRows={7}
              value={pickup.notes}
              onChange={handleFieldChange("specialNotes")}
              variant="outlined"
              helperText="Write any special instructions for driver..."
            />
          </>
        )}
      </AssistanceBox>

      {/* DELIVERY TIME SLOT */}
      <SectionTitle>
        <IconWrapper>
          <DescriptionIcon />
        </IconWrapper>
        <SectionLabel>Pickup Time Slot</SectionLabel>
      </SectionTitle>
      <FormGrid>
        <StyledTextField
          fullWidth
          type="date"
          label="Start Date"
          value={pickup.startDate}
          onChange={handleFieldChange("startDate")}
          error={!!errors?.startDate}
          helperText={errors?.startDate}
          variant="outlined"
        />
        <StyledTextField
          fullWidth
          type="date"
          label="End Date"
          value={pickup.endDate}
          onChange={handleFieldChange("endDate")}
          error={!!errors?.endDate}
          helperText={errors?.endDate}
          variant="outlined"
        />
        <StyledTextField
          fullWidth
          select
          label="Pickup Time Slot *"
          value={pickup.pickupTimeSlot || "None"}
          onChange={handleSelectChange("pickupTimeSlot")}
          error={!!errors?.timeSlot}
          helperText={errors?.timeSlot}
          variant="outlined"
        >
          {timeSlots.map((o) => (
            <MenuItem key={o} value={o}>
              {o}
            </MenuItem>
          ))}
        </StyledTextField>
      </FormGrid>

      {/* Notes Section */}
      <SectionTitle>
        <IconWrapper>
          <NotesIcon />
        </IconWrapper>
        <SectionLabel>Pickup Notes</SectionLabel>
      </SectionTitle>
      <NotesBox>
        <Box
          sx={{
            padding: "16px",
            borderRadius: "12px",
            backgroundColor: "rgba(255,193,7,0.08)",
            border: "1px solid rgba(255,193,7,0.3)",
            display: "flex",
            gap: "12px",
            alignItems: "flex-start",
            mb: 5,
          }}
        >
          {/* ICON */}
          <Box
            sx={{
              width: "28px",
              height: "28px",
              minWidth: "28px",
              borderRadius: "6px",
              backgroundColor: "rgba(255,193,7,0.25)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mt: "2px",
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 20, color: "#b7791f" }} />
          </Box>

          {/* TEXT CONTENT */}
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "15px",
                mb: 0.5,
                color: "#8a6d3b",
              }}
            >
              Pickup Notes — What to include
            </Typography>

            {/* 2-COLUMN LIST */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "1fr 1fr",
                  md: "1fr 1fr 1fr",
                },
                rowGap: "8px",
                columnGap: "16px",
              }}
            >
              <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                • Gate access instructions
              </Typography>

              <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                • Loading timings or restrictions
              </Typography>

              <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                • Safety gear requirements
              </Typography>

              <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                • Required documents or ID
              </Typography>

              <Typography sx={{ fontSize: "14px", color: "#6c757d" }}>
                • Instructions for fragile/heavy cargo
              </Typography>
            </Box>
          </Box>
        </Box>

        <StyledTextField
          fullWidth
          label="Pickup Notes (Important) *"
          multiline
          minRows={7}
          value={pickup.notes}
          onChange={handleFieldChange("pickupNotes")}
          variant="outlined"
          helperText={
            !!errors?.notes
              ? "Write any instructions that help the driver during pickup."
              : errors?.notes
          }
          error={!!errors?.notes}
        />
      </NotesBox>

      {/* FILE UPLOADS */}
      <SectionTitle>
        <IconWrapper>
          <NotesIcon />
        </IconWrapper>
        <SectionLabel>Pickup Images/Documents</SectionLabel>
      </SectionTitle>

      <FullWidthGrid sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* Image Upload Section */}
        <Box>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
            disabled={pickup.images?.length >= 5}
            sx={{ mb: 1 }}
          >
            Upload Images ({pickup.images?.length || 0}/5)
            <input
              type="file"
              hidden
              multiple
              accept="image/*"
              onChange={handleFileUpload("images")}
            />
          </Button>

          {pickup.images?.length >= 5 && (
            <Typography color="error" variant="caption" sx={{ marginLeft: 2 }}>
              Maximum of 5 images reached.
            </Typography>
          )}

          <Box
            mt={2}
            sx={{
              display: "flex",
              gap: 5,
              overflowX: "auto",
              paddingBottom: 1,
              "&::-webkit-scrollbar": { height: 6 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#ccc",
                borderRadius: 10,
              },
            }}
          >
            {pickup.images?.map((file, index) => (
              <Box
                key={index}
                sx={{
                  position: "relative",
                  minWidth: "180px",
                  height: "180px",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid #ddd",
                  flexShrink: 0,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => handleDeleteFile("images", index)}
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    background: "rgba(0,0,0,0.6)",
                    color: "#fff",
                    "&:hover": {
                      background: "rgba(0,0,0,0.8)",
                    },
                  }}
                >
                  <Trash2 fontSize="small" />
                </IconButton>

                <img
                  src={URL.createObjectURL(file)}
                  alt={`preview-${index}`}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>

        {/* Document Upload Section */}
        <Box>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
            disabled={pickup.documents?.length >= 5}
            sx={{ mb: 1 }}
          >
            Upload Documents ({pickup.documents?.length || 0}/5)
            <input
              type="file"
              hidden
              multiple
              onChange={handleFileUpload("documents")}
            />
          </Button>

          {pickup.documents?.length >= 5 && (
            <Typography color="error" variant="caption" sx={{ marginLeft: 2 }}>
              Maximum of 5 documents reached.
            </Typography>
          )}

          <Box mt={2} sx={{ display: "flex", gap: 2, overflowX: "auto" }}>
            {pickup.documents?.map((file, index) => {
              const ext = file.name.split(".").pop().toLowerCase();

              const getIcon = () => {
                if (ext === "pdf") return <PictureAsPdfIcon color="error" />;
                if (["doc", "docx"].includes(ext))
                  return <DescriptionIcon color="primary" />;
                if (["xls", "xlsx"].includes(ext))
                  return <GridOnIcon color="success" />;
                return <InsertDriveFileIcon />;
              };

              return (
                <Box
                  key={index}
                  sx={{
                    minWidth: "200px",
                    borderRadius: 2,
                    border: "1px solid #ddd",
                    padding: 2,
                    background: "#fafafa",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {getIcon()}
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {file.name}
                    </Typography>
                  </Box>

                  <IconButton
                    size="small"
                    onClick={() => handleDeleteFile("documents", index)}
                    sx={{ color: "red" }}
                  >
                    <Trash2 fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        </Box>
      </FullWidthGrid>
      {/* OPTIONAL SECTION */}
      {!hideExtraPickupFields && pickup.pickupType === "third-party" && (
        <>
          <SectionTitle>
            <IconWrapper>
              <DirectionsCarFilledIcon />
            </IconWrapper>
            <SectionLabel>Additional Vehicle & Pickup Details</SectionLabel>
          </SectionTitle>

          <SectionTitle>
            <IconWrapper>
              <LocalShippingIcon />
            </IconWrapper>
            <SectionLabel> Vehicle Details</SectionLabel>
          </SectionTitle>

          <FourColGrid>
            <StyledTextField
              fullWidth
              label="Vehicle Reg Number"
              value={pickup.registrationNo}
              onChange={handleFieldChange("registrationNo")}
              error={!!errors?.registrationNo}
              helperText={errors?.registrationNo}
            />
            <StyledTextField
              fullWidth
              select
              label="Vehicle Type"
              value={pickup.vehicleType || "None"}
              onChange={handleSelectChange("vehicleType")}
              error={!!errors?.vehicleType}
              helperText={errors?.vehicleType}
            >
              {truckTypes.map((o) => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </StyledTextField>
            <StyledTextField
              fullWidth
              label="Driver Name"
              value={pickup.driverName}
              onChange={handleFieldChange("driverName")}
              error={!!errors?.driverName}
              helperText={errors?.driverName}
            />
            <StyledTextField
              fullWidth
              label="Driver Phone"
              value={pickup.driverPhone}
              onChange={handleFieldChange("driverPhone")}
              error={!!errors?.driverPhone}
              helperText={errors?.driverPhone}
            />
          </FourColGrid>

          <Divider sx={{ my: 2 }} />

          <SectionTitle>
            <IconWrapper>
              <DescriptionIcon />
            </IconWrapper>
            <SectionLabel>Pickup Docket</SectionLabel>
          </SectionTitle>

          <FourColGrid>
            <StyledTextField
              fullWidth
              label="Pickup Confirmation Number"
              value={pickup.confirmationNumber}
              onChange={handleFieldChange("confirmationNumber")}
              error={!!errors?.confirmationNumber}
              helperText={errors?.confirmationNumber}
            />
            <StyledTextField
              fullWidth
              label="Carrier"
              value={pickup.carrier}
              onChange={handleFieldChange("carrier")}
              error={!!errors?.carrier}
              helperText={errors?.carrier}
            />
          </FourColGrid>

          <SectionTitle>
            <IconWrapper>
              <NotesIcon />
            </IconWrapper>
            <SectionLabel>Con Note</SectionLabel>
          </SectionTitle>

          <StyledTextField
            fullWidth
            label="Con Note (Important) *"
            multiline
            minRows={7}
            value={pickup.conNote}
            onChange={handleFieldChange("conNote")}
            variant="outlined"
            helperText={
              !!errors?.conNote
                ? "Write any instructions that help the driver during pickup."
                : errors?.conNote
            }
            error={!!errors?.conNote}
          />
        </>
      )}
    </StyledPaper>
  );
}
